import { IssueOnSingleView } from 'components/dataProviders/withIssue/IssueOnSingleView';
import { useCurrentIssue, useCurrentIssueDocuments } from 'components/dataProviders/withIssue';
import { createContext, ReactElement, ReactNode, useContext, useMemo } from 'react';
import { useRetainedValue } from 'hooks/useRetainedValue';
import { useInspection } from 'store/inspection';
import { useInspectionIssues } from 'store/issue';
import { computeInspectionSectionAvailability } from '../inspectionSectionAvailability';
import { useMultiIssueFilteredIssues } from './multiIssueFilteredIssuesContext';
import {
  allMessageAttachmentsAvailable,
  allSectionsAvailable,
  areAvailabilityMapsEqual,
  areMessageAttachmentsMapsEqual,
  computeMessageAttachmentsAvailabilityFromModels,
  computeReportSectionAvailability,
  computeSingleIssueReportAvailability,
  ReportMessageAttachmentsAvailabilityMap,
  ReportSectionAvailabilityMap,
  ReportSectionId,
} from '../model';

// Default is fail-open: consumers without a provider (e.g. inspection report) must not
// have their saved sections greyed out / auto-disabled.
const ReportSectionAvailabilityContext = createContext<ReportSectionAvailabilityMap>(
  allSectionsAvailable()
);

const ReportMessageAttachmentsAvailabilityContext =
  createContext<ReportMessageAttachmentsAvailabilityMap>(
    allMessageAttachmentsAvailable()
  );

const ALL_SECTIONS_AVAILABLE = allSectionsAvailable();
const ALL_MESSAGE_ATTACHMENTS_AVAILABLE = allMessageAttachmentsAvailable();

export function useReportSectionAvailability(): ReportSectionAvailabilityMap {
  return useContext(ReportSectionAvailabilityContext);
}

export function useReportMessageAttachmentsAvailability(): ReportMessageAttachmentsAvailabilityMap {
  return useContext(ReportMessageAttachmentsAvailabilityContext);
}

export function MultiIssueReportAvailabilityProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  // Shares the single filtered-issues fetch with the page-estimate provider.
  const { items, issueModels, loaded } = useMultiIssueFilteredIssues();

  // Fail-open until the filtered issues load so saved sections are not auto-disabled.
  const availability = useMemo<ReportSectionAvailabilityMap>(
    () =>
      loaded
        ? computeReportSectionAvailability(items, false, 0, issueModels)
        : allSectionsAvailable(),
    [items, issueModels, loaded]
  );

  const messageAttachmentsAvailability =
    useMemo<ReportMessageAttachmentsAvailabilityMap>(
      () =>
        loaded
          ? computeMessageAttachmentsAvailabilityFromModels(issueModels)
          : allMessageAttachmentsAvailable(),
      [issueModels, loaded]
    );

  return (
    <ReportSectionAvailabilityContext.Provider value={availability}>
      <ReportMessageAttachmentsAvailabilityContext.Provider
        value={messageAttachmentsAvailability}
      >
        {children}
      </ReportMessageAttachmentsAvailabilityContext.Provider>
    </ReportSectionAvailabilityContext.Provider>
  );
}

export function SingleIssueReportAvailabilityProvider({
  open,
  children,
}: {
  open: boolean;
  children: ReactNode;
}): ReactElement {
  const issue = useCurrentIssue();
  const { documents } = useCurrentIssueDocuments();
  const next = useMemo(
    () => (open ? computeSingleIssueReportAvailability(issue, documents) : undefined),
    [open, issue, documents]
  );
  const value =
    useRetainedValue(next?.sections, areAvailabilityMapsEqual) ?? ALL_SECTIONS_AVAILABLE;
  const messageAttachmentsValue =
    useRetainedValue(next?.messageAttachments, areMessageAttachmentsMapsEqual) ??
    ALL_MESSAGE_ATTACHMENTS_AVAILABLE;

  return (
    <ReportSectionAvailabilityContext.Provider value={value}>
      <ReportMessageAttachmentsAvailabilityContext.Provider
        value={messageAttachmentsValue}
      >
        {children}
      </ReportMessageAttachmentsAvailabilityContext.Provider>
    </ReportSectionAvailabilityContext.Provider>
  );
}

export function InspectionReportAvailabilityProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  const {
    inspection,
    inspectionTemplateDocuments,
    inspectionDocuments,
    inspectionDocumentsLoading,
  } = useInspection();
  const { issues, loading: issuesLoading } = useInspectionIssues();

  const availability = useMemo<ReportSectionAvailabilityMap>(() => {
    if (inspectionDocumentsLoading || issuesLoading) {
      return allSectionsAvailable();
    }

    return computeInspectionSectionAvailability({
      inspection,
      inspectionTemplateDocuments,
      inspectionDocuments,
      issuesOnInspection: issues,
    });
  }, [
    inspection,
    inspectionTemplateDocuments,
    inspectionDocuments,
    inspectionDocumentsLoading,
    issues,
    issuesLoading,
  ]);

  return (
    <ReportSectionAvailabilityContext.Provider value={availability}>
      <ReportMessageAttachmentsAvailabilityContext.Provider
        value={allMessageAttachmentsAvailable()}
      >
        {children}
      </ReportMessageAttachmentsAvailabilityContext.Provider>
    </ReportSectionAvailabilityContext.Provider>
  );
}

export function computeAvailabilityForDemoIssue(
  issue: IssueOnSingleView,
  issueDocumentsCount = 0
): ReportSectionAvailabilityMap {
  return computeReportSectionAvailability([issue], true, issueDocumentsCount);
}
